import { isEmpty } from 'lodash'
import { ComponentProps } from 'react'
import { createRoot } from 'react-dom/client'
import TagManager from 'react-gtm-module'
import { IntlProvider, ReactIntlErrorCode } from 'react-intl'
import { Provider } from 'react-redux'
import { BrowserRouter as Router } from 'react-router-dom'

import App from './components/App'
import routes from './routes'
import { setupStore } from './store'
import translations from './translations'

import './semantic-ui/semantic.css'
import './index.scss'
import 'tailwindcss/tailwind.css'
// i18next (we are slowly moving to this, client-components requires this to be setup)
import './i18n'

import { ErrorBoundary } from 'react-error-boundary'
import { QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'

import ErrorComponent from './components/ErrorComponent'
import Page from './components/Page'
import { NotificationProvider } from './hooks/useNotification'
import { setupQueryClient } from './queryClient'

// Initialise Google Tag manager script
const gtm_id = (host: string) => {
  if (
    host === 'workgridsoftware.dev.workgrid.com' ||
    host === 'workgridsoftware.test.workgrid.com' ||
    host === 'localhost'
  ) {
    return 'GTM-N5S5QJH'
  } else {
    return 'GTM-TP7S3HK'
  }
}
const tagManagerArguments = {
  gtmId: `${gtm_id(window.location.hostname)}`
}
TagManager.initialize(tagManagerArguments)

/*
 * Define user's language. Different browsers have the user locale defined on different fields on the `navigator`
 * object, so we make sure to account for these different by checking all of them. Try full locale, try locale without
 * region code, fallback to 'en'.
 */
const language = (navigator.languages && navigator.languages[0]) || navigator.language
const languageWithoutRegionCode = language.toLowerCase().split(/[_-]+/)[0]
const languageTranslations = translations[language] || translations[languageWithoutRegionCode] || translations.en
const intlProps: ComponentProps<typeof IntlProvider> = isEmpty(languageTranslations)
  ? { locale: 'en' }
  : { locale: language, messages: languageTranslations }

const store = setupStore()

const queryClient = setupQueryClient({
  getTenantHostUrl: () => store.getState().configuration.apiHost!,
  getAccessToken: () => store.getState().authentication.accessToken!,
  getCurrentSpaceId: () => store.getState().context.spaceId,
  getCurrentOrganizationId: () => store.getState().context.organization.id
})

const root = createRoot(document.querySelector('#root')!)
root.render(
  <Provider store={store}>
    <IntlProvider
      textComponent="span"
      {...intlProps}
      onError={error => {
        // Silence missing translation errors in development
        if (process.env.NODE_ENV === 'development' && error.code === ReactIntlErrorCode.MISSING_TRANSLATION) {
          return
        }

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error(error)
        }
      }}
    >
      {/* @ts-expect-error */}
      <ErrorBoundary
        fallback={
          <Page>
            <ErrorComponent
              errors={[
                {
                  code: 'Generic',
                  title: 'An error has occurred',
                  message: 'Something went wrong...',
                  subMessage:
                    "It's not you, it's us. There was a problem with the system which caused your request to fail."
                }
              ]}
            />
          </Page>
        }
      >
        <Router basename={process.env.PUBLIC_URL}>
          <QueryClientProvider client={queryClient}>
            <NotificationProvider>
              <App routes={routes} />
            </NotificationProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </Router>
      </ErrorBoundary>
    </IntlProvider>
  </Provider>
)
